import React, { useState } from 'react';
import useUpgradeDialog from '../../../hooks/useUpgradeDialog';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  ModalOverlay,
  ModalContainer,
  CloseButton,
  LeftSection,
  RightSection,
  PurpleBanner,
  MainTitle,
  Subtitle,
  EmailSection,
  EmailInput,
  AddEmailLink,
  SendInvitesButton,
  SkipLink,
  ClassroomIllustration,
  FeatureBubbles,
  FeatureBubble,
  ErrorText,
  EmailFieldContainer,
  IllustrationImage,
  ErrorTextWithMargin,
  ButtonContainer,
  RemoveEmailButton
} from './style';
import TeacherClassroomIllustration from '../../../static/img/TeacherClassroomIllustration.svg';
import { sendReferralInvites } from '../../../service.js';
import closeIcon from '../../../static/icons/closeIcon.png';


const emailValidationSchema = Yup.object().shape({
  emails: Yup.array().of(
    Yup.string()
      .nullable()
      .transform(value => (value === '' ? undefined : value))
      .test('email-required', 'Email address is required', function(value) {
        const path = this.path || '';
        const match = path.match(/emails\[(\d+)\]/);
        const index = match ? parseInt(match[1]) : -1;
        if (index >= 0 && index < 3) {
          if (!value || value.trim() === '') {
            return false;
          }
        }
        return true;
      })
      .test('email-format', 'Please enter a valid email', function(value) {
        if (!value || value.trim() === '') {
          return true;
        }
        return Yup.string().email().isValidSync(value.trim());
      })
  ).test(
    'at-least-3-required-emails',
    'Please enter at least 3 different valid emails to unlock this offer!',
    function(value) {
      if (!value) return false;
      const validEmails = value.filter(email => {
        if (!email || email.trim() === '') return false;
        return Yup.string().email().isValidSync(email.trim());
      });
      return new Set(validEmails.map(email => email.trim().toLowerCase())).size >= 3;
    }
  )
});

const TeacherReferralPopup = ({ 
  isOpen, 
  onClose, 
  onSendInvites,
  onSkip 
}) => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [requestError, setRequestError] = useState(null);
  // Sending invites and skipping to checkout are exclusive, and closing mid-request would
  // strand the redirect or its error, so the dialog stays put until the request settles.
  const isBusy = isRedirecting || isSending;
  const close = () => { if (!isBusy && onClose) onClose(); };

  const dialogRef = useUpgradeDialog(isOpen, close);
  if (!isOpen) return null;

  const initialValues = {
    emails: ['', '', '']
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (isRedirecting) { setSubmitting(false); return; }
    setRequestError(null);
    setIsSending(true);
    try {
      const validEmails = values.emails.filter(email => email?.trim()).map(email => email.trim().toLowerCase());
      const result = await sendReferralInvites(validEmails);
      
      if (!result?.couponCode) throw new Error('Invitations could not be sent. Please try again.');
      if (result) {
        if (onSendInvites) {
          onSendInvites(result);
        }
      }
    } catch (error) {
      setRequestError(error.message || 'Invitations could not be sent. Please try again.');
    } finally {
      setIsSending(false);
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (isBusy) return;
    setIsRedirecting(true);
    setRequestError(null);
    try {
      if (onSkip) await onSkip();
    } catch (error) {
      setRequestError('We could not open checkout. Please try again.');
      setIsRedirecting(false);
    }
  };

  return (
    <ModalOverlay>
      <ModalContainer ref={dialogRef} role="dialog" aria-modal="true" aria-label="Invite teachers" tabIndex={-1}>
        <CloseButton aria-label="Close" onClick={close} disabled={isBusy}>×</CloseButton>
        
        <LeftSection>
          <PurpleBanner>INVITE OTHER TEACHERS</PurpleBanner>
          <MainTitle>Save 50% on your first Pro payment</MainTitle>
          <Subtitle>Refer 3 teachers to unlock the offer</Subtitle>
          
          <Formik
            initialValues={initialValues}
            validationSchema={emailValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, setFieldValue, isSubmitting }) => {
              const validEmails = values.emails.filter(email => {
                if (!email || email.trim() === '') return false;
                return Yup.string().email().isValidSync(email.trim());
              });
              const validEmailCount = new Set(validEmails.map(email => email.trim().toLowerCase())).size;
              const hasEnteredEmails = values.emails.some(email => email && email.trim() !== '');
              
              return (
                <Form>
                  <EmailSection>
                    <h3>Invite other teachers</h3>
                    
                    {values.emails.map((email, index) => (
                      <EmailFieldContainer key={index}>
                        <Field
                          as={EmailInput}
                          name={`emails.${index}`}
                          type="email"
                          placeholder={`${index + 1}${index === 0 ? 'st' : index === 1 ? 'nd' : index === 2 ? 'rd' : 'th'} Email`}
                          hasError={Array.isArray(errors.emails) && errors.emails[index] && touched.emails && touched.emails[index]}
                          autoComplete="off"
                          data-lpignore="true"
                          data-form-type="other"
                        />
                        {index >= 3 && (
                          <RemoveEmailButton
                            type="button"
                            onClick={() => {
                              const newEmails = values.emails.filter((_, i) => i !== index);
                              setFieldValue('emails', newEmails);
                            }}
                          >
                            <img src={closeIcon} alt="Remove email" />
                          </RemoveEmailButton>
                        )}
                        {index < 3 && Array.isArray(errors.emails) && errors.emails[index] && touched.emails && touched.emails[index] &&
                          !(index === 2 && errors.emails[index] === 'Email address is required' && validEmailCount < 3 && hasEnteredEmails) && (
                            <ErrorText>{errors.emails[index]}</ErrorText>
                          )}
                      </EmailFieldContainer>
                    ))}
                    
                    {(errors.emails && typeof errors.emails === 'string') || (validEmailCount < 3 && hasEnteredEmails) ? (
                      <ErrorTextWithMargin>
                        {errors.emails && typeof errors.emails === 'string' 
                          ? errors.emails 
                          : 'Please enter at least 3 different valid emails to unlock this offer!'}
                      </ErrorTextWithMargin>
                    ) : null}
                    
                    <ButtonContainer>
                      <AddEmailLink 
                        type="button"
                        disabled={values.emails.length >= 10 || isSubmitting}
                        onClick={() => {
                          const newEmails = [...values.emails, ''];
                          setFieldValue('emails', newEmails);
                        }}
                      >
                        +Add another email
                      </AddEmailLink>
                      <SendInvitesButton 
                        type="submit" 
                        disabled={isSubmitting || isRedirecting || validEmailCount < 3}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Invites'}
                      </SendInvitesButton>
                    </ButtonContainer>
                  </EmailSection>
                  {requestError && <ErrorTextWithMargin role="alert">{requestError}</ErrorTextWithMargin>}
                  <SkipLink type="button" onClick={handleSkip} disabled={isBusy || isSubmitting}>Not interested - Skip offer</SkipLink>
                </Form>
              );
            }}
          </Formik>
        </LeftSection>

        <RightSection>
         <IllustrationImage 
           src={TeacherClassroomIllustration} 
           alt="Teacher Classroom Illustration" 
         />
        </RightSection>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TeacherReferralPopup;
